/**
 * Derivações: status, tempo real e progresso saem SEMPRE dos eventos.
 *
 * Esta é a regra que sustenta o offline inteiro (PRD RN-020, RN-030). O
 * aparelho grava "a etapa X começou às 14:02 pelo operador Y" — um fato, que
 * não conflita com fato nenhum. "A etapa está em execução" é conclusão, e
 * conclusão se recalcula. Dois tablets sem rede podem gravar sobre a mesma
 * etapa e o resultado continua correto quando as duas filas sobem.
 *
 * O mesmo cálculo existe em SQL (`pcpgbu_etapa_recalcular`). Os dois precisam
 * concordar: `derivacoes.test.ts` e as provas do banco usam os mesmos casos.
 */

import type {
  EstadoEtapa,
  EstadoOs,
  EventoProducao,
  MarcaEtapa,
  Os,
  OsEtapa,
  Parametros,
  StatusOs,
} from './tipos.js';

const ms = (iso: string): number => Date.parse(iso);

/**
 * Ordem canônica dos eventos: pelo instante em que ACONTECERAM, não pelo em
 * que chegaram. É o que faz a fila de um tablet que ficou 5 h sem rede se
 * encaixar no lugar certo da história.
 *
 * Empate desempata pelo id, que é estável — assim dois aparelhos chegam ao
 * mesmo resultado sem combinar nada.
 */
export function ordenarEventos(eventos: readonly EventoProducao[]): EventoProducao[] {
  return [...eventos].sort((a, b) => {
    const d = ms(a.ocorridoEm) - ms(b.ocorridoEm);
    return d !== 0 ? d : a.id.localeCompare(b.id);
  });
}

/**
 * Tira de cena os eventos estornados e os próprios estornos.
 *
 * Estorno não apaga: o evento continua no histórico e na linha do tempo
 * (RN-021). Ele só deixa de contar para o estado.
 */
export function eventosEfetivos(eventos: readonly EventoProducao[]): EventoProducao[] {
  const estornados = new Set<string>();
  for (const e of eventos) {
    if (e.tipo === 'estorno' && e.estornaEventoId) estornados.add(e.estornaEventoId);
  }
  return ordenarEventos(eventos.filter((e) => e.tipo !== 'estorno' && !estornados.has(e.id)));
}

export interface OpcoesEstadoEtapa {
  /** Instante de referência para a etapa que está correndo agora. */
  agora?: string;
  /** Quantidade prevista, para a marca de quantidade acima do previsto. */
  quantidadePrevista?: number | null;
  parametros?: Pick<Parametros, 'toleranciaQuantidadePct'>;
}

/**
 * O estado de uma etapa a partir dos seus eventos.
 *
 * Regras que valem a pena ler antes de mexer:
 *
 *  - **Início repetido não reinicia o cronômetro.** Dois tablets offline podem
 *    iniciar a mesma etapa; o primeiro abre o intervalo e o segundo só entra
 *    como operador e marca `inicio_concorrente`. Reiniciar perderia o tempo já
 *    corrido, que é justamente o que se quer medir.
 *  - **Pausa fecha o intervalo; retomada abre outro.** O tempo real é a soma
 *    dos intervalos, então pausa não conta (RN-022).
 *  - **Conclusão depois de conclusão** não muda o status nem o tempo; fica como
 *    `conclusao_duplicada` para o encarregado olhar. Recusar seria pior: o
 *    segundo apontamento existe e alguém precisa vê-lo.
 */
export function estadoDaEtapa(
  eventos: readonly EventoProducao[],
  opcoes: OpcoesEstadoEtapa = {},
): EstadoEtapa {
  const agora = opcoes.agora ? ms(opcoes.agora) : Date.now();
  const efetivos = eventosEfetivos(eventos);

  let status: EstadoEtapa['status'] = 'fila';
  let iniciadaEm: string | null = null;
  let concluidaEm: string | null = null;
  let abertoDesde: number | null = null;
  let tempo = 0;
  let qtdBoa: number | null = null;
  let qtdRefugo: number | null = null;
  let motivoPausaId: string | null = null;
  const operadores: string[] = [];
  const marcas = new Set<MarcaEtapa>();

  const abrir = (e: EventoProducao) => {
    if (abertoDesde === null) abertoDesde = ms(e.ocorridoEm);
    else marcas.add('inicio_concorrente');
  };
  const fechar = (e: EventoProducao) => {
    if (abertoDesde !== null) {
      tempo += Math.max(0, ms(e.ocorridoEm) - abertoDesde);
      abertoDesde = null;
    }
  };
  const registrarOperador = (id: string | null) => {
    if (id && !operadores.includes(id)) operadores.push(id);
  };

  for (const e of efetivos) {
    switch (e.tipo) {
      case 'inicio':
        if (status === 'concluida' || status === 'pulada') {
          // Reabertura: alguém iniciou uma etapa já encerrada. Vale, e o
          // encarregado vê pela linha do tempo.
          concluidaEm = null;
        }
        if (iniciadaEm === null) iniciadaEm = e.ocorridoEm;
        abrir(e);
        registrarOperador(e.pessoaId);
        motivoPausaId = null;
        status = 'em_execucao';
        break;

      case 'retomada':
        abrir(e);
        registrarOperador(e.pessoaId);
        motivoPausaId = null;
        status = 'em_execucao';
        break;

      case 'pausa':
        fechar(e);
        motivoPausaId = e.motivoId;
        if (e.texto === '__encerrada_automaticamente__') marcas.add('encerrada_automaticamente');
        status = 'pausada';
        break;

      case 'conclusao':
        if (status === 'concluida') marcas.add('conclusao_duplicada');
        fechar(e);
        concluidaEm = e.ocorridoEm;
        if (e.qtdBoa !== null) qtdBoa = e.qtdBoa;
        if (e.qtdRefugo !== null) qtdRefugo = e.qtdRefugo;
        registrarOperador(e.pessoaId);
        motivoPausaId = null;
        status = 'concluida';
        break;

      case 'pulo':
        fechar(e);
        concluidaEm = e.ocorridoEm;
        motivoPausaId = null;
        status = 'pulada';
        break;

      case 'estorno':
        // Já removido por eventosEfetivos.
        break;
    }
  }

  if (abertoDesde !== null) tempo += Math.max(0, agora - abertoDesde);

  const previsto = opcoes.quantidadePrevista ?? null;
  const tolerancia = (opcoes.parametros?.toleranciaQuantidadePct ?? 10) / 100;
  if (previsto !== null && previsto > 0 && qtdBoa !== null) {
    const total = qtdBoa + (qtdRefugo ?? 0);
    if (total > previsto * (1 + tolerancia)) marcas.add('quantidade_acima_do_previsto');
  }

  return {
    status,
    iniciadaEm,
    concluidaEm,
    tempoRealSeg: Math.round(tempo / 1000),
    qtdBoa,
    qtdRefugo,
    operadores,
    motivoPausaId,
    marcas: [...marcas],
  };
}

/** Só as etapas que alguém executa. Linhas de custo não entram em conta nenhuma. */
export function etapasDeOperacao(etapas: readonly OsEtapa[]): OsEtapa[] {
  return etapas.filter((e) => e.tipoLinha === 'operacao');
}

/** A ordem em que as etapas de uma OS acontecem: seção, depois a ordem impressa. */
export function ordenarEtapasDaOs(etapas: readonly OsEtapa[]): OsEtapa[] {
  const peso: Record<string, number> = { pre_impressao: 0, impressao: 1, acabamento: 2 };
  return [...etapas].sort((a, b) => {
    const d = (peso[a.secao] ?? 9) - (peso[b.secao] ?? 9);
    return d !== 0 ? d : a.sequencia - b.sequencia;
  });
}

export interface OpcoesEstadoOs {
  agora?: string;
  /** Estado já calculado de cada etapa, por id. */
  estados: ReadonlyMap<string, EstadoEtapa>;
}

/**
 * O estado da OS sai do estado das etapas. Nada aqui é decidido por alguém
 * clicando "mudar status" — exceto cancelar e expedir, que são fatos externos
 * à produção e vivem no próprio registro da OS.
 */
export function estadoDaOs(
  os: Pick<Os, 'status' | 'canceladaEm' | 'concluidaEm'>,
  etapas: readonly OsEtapa[],
  opcoes: OpcoesEstadoOs,
): EstadoOs {
  const operacao = ordenarEtapasDaOs(etapasDeOperacao(etapas));
  const est = (e: OsEtapa) => opcoes.estados.get(e.id);

  let concluidas = 0;
  let restante = 0;
  let total = 0;
  let real = 0;
  let prontaEm: string | null = null;
  let alguemComecou = false;
  let etapaAtualId: string | null = null;
  let proximaDaFila: string | null = null;

  for (const etapa of operacao) {
    const s = est(etapa);
    const previsto = etapa.tempoPrevistoSeg ?? 0;
    total += previsto;
    real += s?.tempoRealSeg ?? 0;

    const terminada = s?.status === 'concluida' || s?.status === 'pulada';
    if (terminada) {
      concluidas += 1;
      const fim = s?.concluidaEm ?? null;
      if (fim && (prontaEm === null || ms(fim) > ms(prontaEm))) prontaEm = fim;
    } else {
      restante += previsto;
      if (proximaDaFila === null) proximaDaFila = etapa.id;
    }

    if (s && s.status !== 'fila') alguemComecou = true;
    if (etapaAtualId === null && s?.status === 'em_execucao') etapaAtualId = etapa.id;
  }

  const todasTerminadas = operacao.length > 0 && concluidas === operacao.length;

  let status: StatusOs;
  if (os.canceladaEm) status = 'cancelada';
  else if (os.concluidaEm) status = 'concluida';
  else if (todasTerminadas) status = 'pronta';
  else if (alguemComecou) status = 'em_producao';
  else status = 'em_fila';

  return {
    status,
    prontaEm: todasTerminadas ? prontaEm : null,
    etapasConcluidas: concluidas,
    etapasTotal: operacao.length,
    tempoPrevistoRestanteSeg: restante,
    tempoPrevistoTotalSeg: total,
    tempoRealTotalSeg: real,
    etapaAtualId: etapaAtualId ?? proximaDaFila,
  };
}

/**
 * A OS passou do prazo?
 *
 * O parâmetro `atrasoContaAte` decide se ficar pronta já tira a OS do atraso
 * ou se o relógio corre até a entrega — é pergunta aberta com o Jeferson
 * (PRD §17.1 J-11), então é parâmetro, não regra escrita no código.
 */
export function osAtrasada(
  os: Pick<Os, 'previsaoEntregaEm'>,
  statusAtual: StatusOs,
  parametros: Pick<Parametros, 'atrasoContaAte'>,
  agora: string = new Date().toISOString(),
): boolean {
  const fora: StatusOs[] =
    parametros.atrasoContaAte === 'pronta'
      ? ['pronta', 'concluida', 'cancelada']
      : ['concluida', 'cancelada'];
  if (fora.includes(statusAtual)) return false;
  return ms(agora) > ms(os.previsaoEntregaEm);
}

/**
 * Cabe no prazo? Compara o tempo previsto que falta com as horas úteis que
 * restam até a promessa. É indicativo — nunca bloqueia nada (RN-032).
 */
export function osEmRisco(
  previsaoEntregaEm: string,
  tempoPrevistoRestanteSeg: number,
  segundosUteisAte: (de: string, ate: string) => number,
  agora: string = new Date().toISOString(),
): boolean {
  if (tempoPrevistoRestanteSeg <= 0) return false;
  if (ms(previsaoEntregaEm) <= ms(agora)) return false;
  return tempoPrevistoRestanteSeg > segundosUteisAte(agora, previsaoEntregaEm);
}

/** Desvio do tempo real contra o previsto, em porcentagem. Null sem previsão. */
export function desvioPct(previstoSeg: number | null, realSeg: number): number | null {
  if (previstoSeg === null || previstoSeg <= 0) return null;
  return ((realSeg - previstoSeg) / previstoSeg) * 100;
}
